import { Sentry } from './lib/sentry'
import './lib/stripe-loader'
import axios from "axios"
import { Carousel, Modal } from "bootstrap"
import { Dropzone } from "dropzone"
import { setOptions, importLibrary } from "@googlemaps/js-api-loader"
import * as Handlers from "./modules/forms/handlers"
import { mdcInputs, setupInputs } from "./modules/forms/materialDesign"

// @ts-ignore
import type { AutocompleteService, AutocompleteSessionToken } from 'google.maps'

declare global {
    interface Window {
        Sentry: typeof Sentry
    }
}

/**
 * Import Bootstrap modal for claiming business
 */
const modal = {
    claim: null
}
if (document.getElementById('claim-business')) {
    modal.claim = new Modal('#claim-business')
}

/**
 * Google Maps JS options
 * @type {HTMLScriptElement}
 */
setOptions({
    key: process.env.GOOGLE_MAPS_KEY,
    v: "weekly",
    libraries: ["places"]
})

const librariesPromise = Promise.all([
    importLibrary("maps"),
    importLibrary("places")
])

const stripeElements = {
    card: null
}

const carousel = {
    active: false,
    element: <HTMLDivElement>document.querySelector('.carousel'),
    bs: null,
    form: <HTMLFormElement>document.querySelector('.carousel form'),
    upgraded: false
}

/**
 * Field data
 */
const fields = {
    logoFile: null,

    // Variables for channel images field
    imageFiles: null,
    ciActionId: <number>70,
    ciSiteId: <number>1,
    ciImagesField: <number>312,

    businessType: <string>null,
    businessName: <string>null,

    autocompleteService: <AutocompleteService|null>null,
    sessionToken: <AutocompleteSessionToken|null>null,
    places: {
        'place': null,
        'timezone': null
    }
}

/**
 * Create URL slug
 * @params {string}
 * @returns {string}
 * @private
 */
const slugify = (string) => {
    const a = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;'
    const b = 'aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------'
    const p = new RegExp(a.split('').join('|'), 'g')

    return string.toString().toLowerCase()
        .replace(/\s+/g, '-') // Replace spaces with -
        .replace(p, c => b.charAt(a.indexOf(c))) // Replace special characters
        .replace(/&/g, '-and-') // Replace & with 'and'
        .replace(/[^\w\-]+/g, '') // Remove all non-word characters
        .replace(/\-\-+/g, '-') // Replace multiple - with single -
        .replace(/^-+/, '') // Trim - from start of text
        .replace(/-+$/, '') // Trim - from end of text
}

/**
 * Handle entry response
 * @param response
 * @private
 */
const entryResponseHandler = (response) => {

    // Handle any field errors first
    let fieldErrors = Object.keys(response.field_errors)
    if (fieldErrors.length > 0) {
        fieldErrors.forEach( (field) => {
            if (field === 'url_title' && modal.claim) {
                modal.claim.show()
            } else if (field === 'business_logo') {
                // Set error
                carousel.form.querySelector<HTMLElement>('[data-dz-errormessage]').innerText = response.field_errors[field]
                carousel.form.querySelector('.dz-preview').classList.add('dz-error')
                // Goto carousel slide
                carousel.element.querySelectorAll('.carousel-item').forEach( (item, index) => {
                    if (item.querySelector('#businessLogo')) {
                        carousel.bs.to(index)
                    }
                })
            } else if (field === 'business_images_all') {

            } else {
                let mdc = mdcInputs[field]
                if (mdc) {
                    mdc.helperTextContent = response.field_errors[field].innerText
                    mdc.valid = false
                }
            }
        })
    }

    let targetFound = false

    // Goto the first error
    carousel.form.querySelectorAll('.carousel-item').forEach( (elem, index) => {
        if (elem.querySelectorAll('.mdc-text-field--invalid').length > 0 && !targetFound) {
            carousel.bs.to(index)
            targetFound = true
        }
    })
}

/**
 * Set the navigation listeners
 */
const setNavigation = () => {

    // Next actions
    const next = document.querySelectorAll('.mdc-next'),
        back = document.getElementById('nav-back')

    next.forEach( (elem: HTMLElement) => {
        elem.addEventListener('click', () => {
            // Get visible .field elements
            let valid = true,
                fieldset = carousel.form.querySelector('.field.active'),
                labels = fieldset.querySelectorAll('label')

            labels.forEach( (label) => {
                let mdc = mdcInputs[label.getAttribute('for')]
                if (typeof mdc !== 'undefined' && mdc.valid === false) {
                    /* Set valid state */
                    valid = false
                    /* Add invalid class */
                    label.classList.add('mdc-text-field--invalid')
                }
            })

            // Get current field
            if (valid && carousel.active) {

                // Determine if we skip a slide
                if (elem.dataset.skip) {
                    carousel.form.querySelectorAll('.carousel-item').forEach( (item, index) => {
                        if (item.classList.contains('active')) {
                            // Navigate carousel to correct slide
                            let goIndex = index + 1 + parseInt(elem.dataset.skip)
                            carousel.bs.to(goIndex)

                            // Update the back button to handle the skip
                            back.classList.add('mdc-skip')
                            back.setAttribute('data-skip', elem.dataset.skip)
                        }
                    })
                } else {
                    carousel.bs.next()
                }
            }

        }, { passive: true })
    })

    // Previous action
    back.addEventListener('click', () => {

        // Move carousel
        if (carousel.active) {
            // Determine if skipping back
            if (back.classList.contains('mdc-skip')) {
                carousel.form.querySelectorAll('.carousel-item').forEach( (item, index) => {
                    if (item.classList.contains('active')) {
                        // Navigate carousel to correct slide
                        let goIndex = index - 1 - parseInt(back.dataset.skip)
                        carousel.bs.to(goIndex)

                        // Remove skip from back nav
                        back.classList.remove('mdc-skip')
                        back.removeAttribute('data-skip')
                    }
                })
            } else {
                carousel.bs.prev()
            }
        }

    }, { passive: true })

    // Update section label on slide transition
    carousel.element.addEventListener('slide.bs.carousel', (ev: any) => {
        let slide = <HTMLElement>carousel.element.querySelectorAll('.carousel-item')[ev.to],
            step: HTMLElement = carousel.element.querySelector('.step-label')
        if (slide.dataset.step) {
            if (slide.dataset.step !== step.innerText) {
                step.innerText = slide.dataset.step
            }
        }
    }, true)
}

/**
 * Custom field listeners
 */
const fieldListeners = () => {

    // Inject business name text
    if (carousel.form.querySelector('[name="title"]')) {
        const title: HTMLInputElement = carousel.form.querySelector('[name="title"]')
        title.addEventListener('change', () => {
            fields.businessName = title.value

            carousel.form.querySelectorAll('.business-title').forEach( (element: HTMLElement) => {
                element.innerText = fields.businessName
            })
        }, true)
    }


    // Setup address autocomplete
    let address: HTMLInputElement = carousel.form.querySelector('#address')
    if (address) {

        let select: HTMLElement = carousel.form.querySelector('#selectAddress'),
            mdcField = mdcInputs['address']

        // Listen for data to autocomplete
        address.addEventListener('keyup', () => {
            // Any manual change make this invalid
            mdcField.useNativeValidation = false
            mdcField.valid = false

            // Run autocomplete
            addressAutocomplete()
        }, true)

        // Listen to select click
        select.addEventListener('click', (ev) => {
            // Take over validation
            mdcField.useNativeValidation = false

            const option = <HTMLLIElement>ev.target

            // Updates the field display and gets the address components for hidden fields
            let placeId, description

            if (option.classList.contains('mdc-list-item__text')) {
                placeId = option.parentElement.dataset.place
                description = option.innerText
            } else {
                placeId = option.dataset.place
                description = option.querySelector<HTMLElement>('.mdc-list-item__text').innerText
            }

            address.value = description
            mdcField.valid = true

            select.parentElement.classList.remove('mdc-menu-autocomplete')
            select.innerHTML = ''

            // Get the place details and map to fields
            let placesService = new google.maps.places.PlacesService(<HTMLDivElement>select.parentElement)
            placesService.getDetails({
                fields: ['address_components', 'geometry', 'formatted_phone_number'],
                placeId: placeId,
                sessionToken: fields.sessionToken
            }, (place, status) => {
                if (status === 'OK') {
                    //  Store the place data for usage later in determining time zone
                    fields.places.place = place

                    // Populate the address component fields
                    let street_number, route, city, county, state, postal_code
                    for (let component of place.address_components) {
                        if (component.types.includes('street_number')) {
                            street_number = component.short_name
                        } else if (component.types.includes('route')) {
                            route = component.short_name
                        } else if (component.types.includes('locality')) {
                            city = component.short_name
                        } else if (component.types.includes('administrative_area_level_2')) {
                            county = component.short_name.replace(' County', '')
                        } else if (component.types.includes('administrative_area_level_1')) {
                            state = component.short_name
                        } else if (component.types.includes('postal_code')) {
                            postal_code = component.short_name
                        }
                    }

                    // Set the address field
                    carousel.form.querySelector<HTMLInputElement>('[name="address"]').value = (typeof street_number != 'undefined') ? street_number + ' ' + route : route
                    carousel.form.querySelector<HTMLInputElement>('[name="business_city"]').value = city
                    carousel.form.querySelector<HTMLInputElement>('[name="business_county"]').value = county
                    carousel.form.querySelector<HTMLInputElement>('[name="business_state"]').value = state
                    carousel.form.querySelector<HTMLInputElement>('[name="business_zip"]').value = postal_code
                } else {
                    fields.places.place = null
                }
            })
        }, true)
    }

    // Setup Logo upload
    let businessLogo = carousel.form.querySelector<HTMLElement>('#businessLogo')
    if (businessLogo) {

        if (fields.logoFile) {
            fields.logoFile.disable()
            fields.logoFile = null
        }

        fields.logoFile = new Dropzone(businessLogo, {
            dictDefaultMessage: 'Upload Image',
            url: carousel.form.action,
            paramName: 'business_logo',
            maxFilesize: 1,
            acceptedFiles: 'image/png, image/jpg, image/jpeg',
            addRemoveLinks: true,
            //hiddenInputContainer: this.form.querySelector('.dropzone-file'),
            autoProcessQueue: false,
            init: function() {
                // Add existing file
                if (businessLogo.dataset.logoUrl) {
                    let mockFile = { name: businessLogo.dataset.logoName, size: businessLogo.dataset.logoSize }
                    this.displayExistingFile(mockFile, businessLogo.dataset.logoUrl)
                }

                // On added file ensure no empty input
                this.on('addedfile', () => {
                    let inputField = businessLogo.querySelector("[name='business_logo']")
                    if (inputField) {
                        inputField.parentNode.removeChild(inputField)
                    }
                })

                // Remove file and set empty input
                this.on('removedfile', () => {
                    // Add an empty input to empty the field
                    let inputField: HTMLInputElement = businessLogo.querySelector("[name='business_logo']")
                    if (inputField) {
                        inputField.value = ''
                    } else {
                        inputField = document.createElement('input')
                        inputField.type = 'hidden'
                        inputField.name = 'business_logo'
                        inputField.value = ''
                        businessLogo.append(inputField)
                    }
                    // Reset the upload button
                    businessLogo.classList.remove('dz-started')
                })
            }
        })

    }

    let businessImages = carousel.form.querySelector('#businessImages')
    if (businessImages) {

        let key = Math.floor(Date.now() / 1000),
            keyField = document.createElement('input')

        keyField.type = 'hidden'
        keyField.name = 'field_id_' + fields.ciImagesField + '[key]'
        keyField.value = key.toString()

        businessImages.appendChild(keyField)

        fields.imageFiles = new Dropzone(businessImages, {
            dictDefaultMessage: 'Upload Image',
            url: '/?ACT=' + fields.ciActionId + '&site_id=' + fields.ciSiteId + '&ajax_method=upload_file&field_id=' + fields.ciImagesField + '&key=' + key,
            paramName: 'channel_images_file',
            maxFiles: 12,
            maxFilesize: 3,
            acceptedFiles: 'image/png, image/jpg, image/jpeg',
            addRemoveLinks: true,
            //hiddenInputContainer: this.form.querySelector('.dropzone-file'),
            autoProcessQueue: true,
            init: function(){
                // TODO: Force submit to disable if items are in the queue
                // Add existing files
                let images = businessImages.querySelectorAll('textarea')
                if (images) {
                    images.forEach((img) => {
                        // Encode the content for the server processing

                        let data = {
                            "title": img.dataset.title ? window.btoa(img.dataset.title) : '',
                            "description": '',
                            "url_title": '',
                            "category": '',
                            "image_id": img.dataset.image_id ? img.dataset.image_id : 0,
                            "filename": img.dataset.filename,
                            "filesize": img.dataset.filesize,
                            "field_id": 312,
                            "entry_id": img.dataset.entry_id,
                            "extension": img.dataset.filename.split('.').pop(),
                            "cifield_1": img.dataset.cifield_1 ? window.btoa(img.dataset.cifield_1) : '',
                            "cifield_2": img.dataset.cifield_2 ? window.btoa(img.dataset.cifield_2) : '',
                            "cifield_3": img.dataset.cifield_3 ? window.btoa(img.dataset.cifield_3) : '',
                            "cifield_4": img.dataset.cifield_4 ? window.btoa(img.dataset.cifield_4) : '',
                            "cifield_5": img.dataset.cifield_5 ? window.btoa(img.dataset.cifield_5) : '',
                            "cover": window.btoa('0'),
                        }

                        img.innerText = JSON.stringify(data)
                        img.value = JSON.stringify(data)

                        // Make the mockfile
                        let mock = { name: img.dataset.filename, size: img.dataset.filesize}
                        this.displayExistingFile(mock, img.dataset.url)
                    })
                }

                this.on('removedfile', (file) => {
                    businessImages.querySelectorAll('textarea').forEach( (textField) => {
                        if (textField.dataset.filename === file.name) {
                            let data = JSON.parse(textField.innerText)
                            if (data.image_id !== 0) {
                                // If there is an image ID it has been uploaded, so we mark to delete
                                data.delete = window.btoa('true')
                                textField.innerText = JSON.stringify(data)
                                textField.value = JSON.stringify(data)
                            } else {
                                // If the image ID is 0 then it's a temp file, and we can drop it completely
                                textField.parentNode.removeChild(textField)
                            }
                        }
                    })
                })
                this.on('error', (file, message) => {
                    Sentry.withScope( (scope: Sentry.Scope) => {
                        scope.setTag('dropzone', "failed")
                        Sentry.captureException(new Error(message))
                    })
                })
                this.on('success', (file, response) => {
                    this.files.forEach( (item, index) => {
                        if (item.name === file.name) {
                            let i = ++index
                            let textField = document.createElement('textarea')
                            textField.classList.add('d-none')
                            textField.name = 'field_id_312[images][' + i + '][data]'
                            textField.dataset.filename = file.name

                            // Build the response
                            let resp = JSON.parse(response)
                            resp.title = resp.title ? window.btoa(resp.title) : ''
                            resp.image_id = resp.image_id ? resp.image_id : '0'
                            resp.url_title = resp.url_title ? window.btoa(resp.url_title) : ''
                            resp.description = resp.description ? window.btoa(resp.description) : ''
                            resp.cifield_1 = resp.cifield_1 ? window.btoa(resp.cifield_1) : ''
                            resp.cifield_2 = resp.cifield_2 ? window.btoa(resp.cifield_2) : ''
                            resp.cifield_3 = resp.cifield_3 ? window.btoa(resp.cifield_3) : ''
                            resp.cifield_4 = resp.cifield_4 ? window.btoa(resp.cifield_4) : ''
                            resp.cifield_5 = resp.cifield_5 ? window.btoa(resp.cifield_5) : ''
                            resp.cover = resp.cover ? window.btoa("" + resp.cover) : window.btoa('0')

                            textField.innerText = JSON.stringify(resp)
                            fields.imageFiles.element.append(textField)
                        }
                    })
                })
            }

        })
    }
}

/**
 * Perform Google Places API autocomplete for address
 * @private
 */
const addressAutocomplete = () => {
    // Abort any requests already running
    if (fields.autocompleteService === null) {
        return
    }

    const address: HTMLInputElement = carousel.form.querySelector('#address')

    // Clear existing select options
    carousel.form.querySelector('#selectAddress').innerHTML = ''

    if (address.value === '') {
        return
    }

    // Update our session token for this query
    fields.sessionToken = new google.maps.places.AutocompleteSessionToken()

    fields.autocompleteService.getPlacePredictions({
        input: address.value,
        componentRestrictions: { country: 'us' },
        types: ['address'],
        sessionToken: fields.sessionToken
    }).then( (response) => {
        let select = carousel.form.querySelector('#selectAddress')
        for (const prediction of response.predictions) {
            let element = document.createElement('li')
            element.classList.add('mdc-list-item', 'mdc-list-item', 'py-2')
            element.dataset.place = prediction.place_id
            element.innerHTML = "<span class=\"mdc-list-item__text\">" + prediction.description + "</span>"

            select.appendChild(element)
        }
        select.parentElement.classList.add('mdc-menu-autocomplete')
    })
}

/**
 * Setup form for listeners and handling
 */
const setupForm = async() => {

    await (window as any).stripePromise

    setupInputs(carousel.form)

    // Add the novalidate attribute since we'll handle that in JS
    carousel.form.noValidate = true

    fieldListeners()

    // Inject business type text
    if (fields.businessType) {
        carousel.form.querySelectorAll('.business-card__type').forEach( (element: HTMLElement) => {
            element.innerText = fields.businessType
        })
    }

    // If the business entry form handle the Stripe component
    if (carousel.form.classList.contains('business-entry')) {

        const upgradeElement: HTMLElement = carousel.form.querySelector('[data-stripe]')
        if (upgradeElement) {

            // Load stripe and set up the card element
            import('./modules/forms/stripe').then( (stripe) => {

                stripeElements.card = stripe.createCard()
                stripe.mountElement(stripeElements.card, upgradeElement)

                // Add button listener
                const form = <HTMLFormElement>document.getElementById('stripe-upgrade')
                const formAlert: HTMLDivElement = upgradeElement.querySelector('.alert')
                const btn: HTMLButtonElement = upgradeElement.querySelector('.btn')

                // Get token
                btn.addEventListener('click', (ev) => {
                    ev.stopPropagation()

                    Handlers.clearAlert(formAlert)

                    btn.disabled = true
                    btn.value = 'Processing...'

                    stripe.createToken(stripeElements.card, form, formAlert)
                        .then( () => {

                            // Set the button to submit
                            btn.type = 'submit'
                            btn.removeAttribute('data-stripe')

                            const formData = new FormData(form)

                            // Simulate the click to upgrade on the site
                            axios.post('/?ACT=' + formData.get('ACT'), formData)
                                .then( () => {

                                    carousel.upgraded = true

                                    // Destroy the element upon success
                                    stripeElements.card.destroy()

                                    formSubmit(carousel.form)

                                }).catch( (error) => {
                                    const p = document.createElement('p')
                                    p.classList.add('mb-0')
                                    p.innerText = error.message

                                    Handlers.setAlertErrors(formAlert, p)

                                    Sentry.withScope( (scope: Sentry.Scope) => {
                                        scope.setTag('axios', "upgrade")
                                        Sentry.captureException(error)
                                    })
                                })
                        })
                        .catch( () => {
                            btn.disabled = false
                            btn.value = btn.dataset.btnText
                        })
                })
            }).catch( (error) => {
                Sentry.withScope( (scope: Sentry.Scope) => {
                    scope.setTag('stripe', "failed")
                    Sentry.captureException(error)
                })
            })
        }
    }

    // Submit form
    carousel.form.addEventListener('submit', (ev) => {
        ev.preventDefault()

        formSubmit(carousel.form)
    })

    // Stop enter key from submitting
    carousel.form.addEventListener('keypress', (ev) => {
        if (ev.key === 'Enter') {
            ev.preventDefault()
            return false
        }
    })

}

/**
 * Card (business-type) listeners
 * @private
 */
const setMdcCardListeners = () => {
    carousel.element.querySelectorAll('.business-card').forEach( (card: HTMLElement) => {

        card.addEventListener('click', () => {

            // Stop if we already made a selection
            carousel.element.querySelectorAll('.business-card').forEach( (elem) => {
                if (elem.classList.contains('disabled')) {
                    return false
                }
            })

            if (card.classList.contains('selected')) {
                // Clicked element already selected so just advance
                if (carousel.active) {
                    carousel.bs.next()
                }
            } else {
                // Grab the business type to update future elements
                fields.businessType = card.querySelector('h6').innerText.toLowerCase()

                // Update UX to show selection
                carousel.element.querySelectorAll('.business-card').forEach( (elem: HTMLElement) => {
                    elem.classList.remove('selected')
                    if (elem.dataset.channel !== card.dataset.channel) {
                        elem.classList.add('disabled')
                    }
                })

                card.classList.add('selected', 'disabled')

                let channel = card.dataset.channel

                // Fetch correct channel form
                axios.get('/business/create/' + channel)
                    .then( (response) => {
                        if (carousel.active) {
                            // Reset the carousel
                            carousel.bs.dispose()
                            carousel.active = false
                        }

                        // Insert form
                        carousel.element.innerHTML = response.data

                        restart()

                        // Remove any styles on cards
                        carousel.element.querySelectorAll('.business-card').forEach( (elem) => {
                            elem.classList.remove('disabled')
                        })
                    })
                    .catch( (error) => {

                        // Handle this error
                        let node = document.createElement('div')
                        node.classList.add('alert alert-primary')
                        node.role = 'alert'
                        node.innerText = 'There was an error selecting the business type. Please contact support.'

                        carousel.element.querySelector('.business-card').before(node)
                        carousel.element.querySelectorAll('.business-card').forEach( (elem) => {
                            elem.classList.remove('selected', 'disabled')
                        })

                        Sentry.withScope( (scope: Sentry.Scope) => {
                            scope.setTag('axios', "upgrade")
                            Sentry.captureException(error)
                        })
                    })
            }

        }, true)
    })
}


/**
 * Form Submit listener
 * @param form
 * @returns {boolean}
 * @private
 */
const formSubmit = (form) => {

    // Validate all fields on form submit
    if (!form.reportValidity()) {
        let inputs = form.querySelectorAll('.mdc-text-field__input')
        inputs.forEach( (input) => {
            if (!input.validity.valid) {
                mdcInputs[input.id].valid = false
            }
        })
        return false
    }

    // Get submit button
    let btn: HTMLButtonElement = carousel.form.querySelector('button[type="submit"]'),
        formAlert = form.querySelector('.alert'),
        formData = new FormData(form)

    btn.disabled = true

    // Handle account creation
    if (form.classList.contains('create-account')) {
        axios.post('/?ACT=' + formData.get('ACT'), formData)
            .then( () => {
                carousel.bs.next()

                btn.disabled = false

            })
            .catch( (error) => {
                if (error.response.status === 403) {
                    const resp = document.createElement('div')
                    resp.innerHTML = error.response.data

                    // Handle registration errors
                    const errors = resp.querySelectorAll('#response li')
                    errors.forEach( (error: HTMLElement) => {
                        if (error.innerText === 'The email you chose is not available' || error.innerText === 'The username you chose is not available') {

                            // Update field props for email field
                            const field = mdcInputs['email'],
                                origValue = field.value

                            if (field) {
                                // Set current state
                                field.helperTextContent = 'The email you chose is not available'
                                field.valid = false

                                // Take over validation to enforce a new email address
                                const native: HTMLInputElement = carousel.form.querySelector('#email')
                                native.addEventListener('keyup', (ev) => {
                                    if ((<HTMLInputElement>ev.target).value === origValue) {
                                        native.setCustomValidity('The email you chose is not available')
                                        field.helperTextContent = 'The email you chose is not available'
                                        field.valid = false
                                    } else if (native.validity.valueMissing) {
                                        native.setCustomValidity('This field is required')
                                        field.helperTextContent = 'This field is required'
                                        field.valid = false
                                    } else if (native.validity.typeMismatch) {
                                        native.setCustomValidity('Enter an email address')
                                        field.helperTextContent = 'Enter an email address'
                                        field.valid = false
                                    } else {
                                        native.setCustomValidity('')
                                        field.helperTextContent = 'This field is required'
                                        field.valid = true
                                    }
                                }, true)
                            }

                            // Goto field
                            const fields = carousel.form.querySelectorAll('.carousel-item.field')
                            fields.forEach((field, index) => {
                                if (field.querySelector('[for="email"]') && carousel.active) {
                                    carousel.bs.to(index)
                                }
                            })
                        } else {
                            carousel.bs.next()
                        }
                    })
                } else {
                    const p = document.createElement('p')
                    p.classList.add('mb-0')
                    p.innerText = error.message

                    Handlers.setAlertErrors(formAlert, p)

                    Sentry.withScope( (scope: Sentry.Scope) => {
                        scope.setTag('axios', "create-account")
                        Sentry.captureException(error)
                    })
                }

                btn.disabled = false
            })
    }

    // Set the URL title using the title and address city
    const urlTitle: HTMLInputElement = carousel.form.querySelector('[name="title"]')
    if (urlTitle) {
        const state: HTMLInputElement = carousel.form.querySelector('[name="business_state"]')
        urlTitle.value = urlTitle.value + '-' + state.value
        formData.set('url_title', slugify(urlTitle.value))
    }

    // Upload the logo file
    if (fields.logoFile && fields.logoFile.files[0]) {
        formData.set('business_logo', fields.logoFile.files[0])
    }

    if (form.classList.contains('create-business')) {

        if (carousel.upgraded) {
            axios.post('/?ACT=' + formData.get('ACT'), formData)
                .then( (response) => {

                    if (!response.data.success) {
                        entryResponseHandler(response.data)

                        // Update "upgrading" language to communicate that account has been upgraded
                        const upgradeElement = carousel.form.querySelector('[data-stripe]')
                        if (upgradeElement) {
                            const alert = upgradeElement.querySelector('.alert')
                            const btn: HTMLButtonElement = upgradeElement.querySelector('.btn')

                            const p = document.createElement('p')
                            p.classList.add('mb-0')
                            p.innerText = 'Thanks for purchasing. Unfortunately there was an error with your business listing. Once corrected please click the save button below.'

                            Handlers.setAlertErrors(alert, p)

                            btn.type = 'submit'
                            btn.innerText = 'Save listing'
                            btn.disabled = false
                        }
                    } else {
                        // Fetch edit form with premium content
                        axios.get('/business/premium/' + response.data.channel_id + '/' + response.data.entry_id)
                            .then( (resp) => {
                                // Reset the carousel
                                carousel.bs.dispose()
                                carousel.active = false

                                // Insert the correct form
                                carousel.element.innerHTML = resp.data

                                // Remove upgrade slides
                                carousel.form.querySelectorAll('[data-step="Upgrade to Premium"]').forEach((item) => {
                                    item.remove()
                                })

                                restart()
                            })
                            .catch( (error) => {
                                throw error
                            })
                    }
                })
                .catch( (error) => {
                    const p = document.createElement('p')
                    p.classList.add('mb-0')
                    p.innerText = error.message

                    const formAlert = form.querySelector('.alert')
                    Handlers.setAlertErrors(formAlert, p)

                    Sentry.withScope( (scope: Sentry.Scope) => {
                        scope.setTag('axios', "create-business")
                        Sentry.captureException(error)
                    })
                })
        } else {
            axios.post('/?ACT=' + formData.get('ACT'), formData)
                .then( (response) => {
                    if (response.data.success) {
                        window.location.href = '/businesses/detail/' + response.data.url_title
                    } else {
                        entryResponseHandler(response.data)
                        // Show button
                        carousel.form.querySelector('button[type="submit"]').removeAttribute('disabled')
                    }
                })
                .catch( (error) => {
                    const p = document.createElement('p')
                    p.classList.add('mb-0')
                    p.innerText = error.message

                    const formAlert = form.querySelector('.alert')
                    Handlers.setAlertErrors(formAlert, p)

                    Sentry.withScope( (scope: Sentry.Scope) => {
                        scope.setTag('axios', "create-business")
                        Sentry.captureException(error)
                    })
                })
        }
    }

    if (form.classList.contains('premium-business')) {

        // Save entry and go to listing
        axios.post('/?ACT=' + formData.get('ACT'), formData)
            .then( (response) => {
                if (response.data.success) {
                    window.location.href = '/businesses/detail/' + response.data.url_title
                } else {
                    entryResponseHandler(response.data)
                    // Show button
                    carousel.form.querySelector('button[type="submit"]').removeAttribute('disabled')
                }
            })
            .catch( (error) => {
                const p = document.createElement('p')
                p.classList.add('mb-0')
                p.innerText = error.message

                const formAlert = form.querySelector('.alert')
                Handlers.setAlertErrors(formAlert, p)

                Sentry.withScope( (scope: Sentry.Scope) => {
                    scope.setTag('axios', "premium-business")
                    Sentry.captureException(error)
                })
            })
    }
}


/**
 * Reinitialize when a new form is loaded dynamically
 * @private
 */
const restart = () => {

    // Grab the new form
    carousel.form = carousel.element.querySelector('form')

    setupForm()

    // Re-init the actions
    setNavigation()
    setMdcCardListeners()

    // Restart the carousel with the new items
    carousel.bs = new Carousel(carousel.element)
    carousel.active = true

    //carousel.bs.next()
}

/**
 * Setup Google Maps
 */
librariesPromise.then(() => {
    fields.autocompleteService = <AutocompleteService>new google.maps.places.AutocompleteService()
})

/**
 * Start everything
 */
setNavigation()

// Card (business type) listener
setMdcCardListeners()

if (carousel.form) {
    setupForm()
}

carousel.bs = new Carousel(carousel.element)
carousel.active = true